import { useState } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Database, Package, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPE_LABELS: Record<string, string> = {
  lab_result: "Resultados Laboratoriais",
  exercise: "Exercícios",
  meal: "Refeições",
  vital_sign: "Sinais Vitais",
  adjustment: "Ajustes",
  protocol: "Protocolos",
  session_note: "Notas da Sessão",
  intervention: "Intervenções",
  assessment: "Avaliações",
  clinical_observation: "Observação Clínica",
  note: "Nota",
  finding: "Achado",
  conclusion: "Conclusão",
  header: "Evento",
  plan_info: "Informações do Plano",
  plan_note: "Notas do Plano",
  measurement: "Métrica",
  plan_summary: "Resumo do Plano"
};

// Função para determinar quais campos mostrar baseado no tipo e raw_json
function getEditableFields(item: HealthRecordItem): EditableFields {
  const itemType = item.item_type;
  const rawJson = (item.raw_json && typeof item.raw_json === 'object' && !Array.isArray(item.raw_json)) 
    ? (item.raw_json as Record<string, any>) 
    : {};
  
  // Para medidas numéricas (vital_sign, lab_result) - removido 'measurement'
  if (itemType === 'vital_sign' || itemType === 'lab_result') {
    return {
      showValue: true,
      showUnit: true,
      showRefLow: true,
      showRefHigh: true,
      showAbnormal: true,
      valueType: 'numeric' as const,
      showTextarea: false
    };
  }
  
  // Para observações clínicas (clinical_observation)
  if (itemType === 'clinical_observation') {
    return {
      showValue: false,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      textLabel: 'Descrição'
    };
  }
  
  // Para exercícios (exercise)
  if (itemType === 'exercise') {
    const hasLoad = rawJson.load !== undefined || rawJson.load !== null;
    const hasSets = rawJson.sets !== undefined;
    const hasReps = rawJson.reps !== undefined;
    
    return {
      showValue: true,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: false,
      showLoad: hasLoad,
      showSets: hasSets,
      showReps: hasReps,
      textLabel: 'Nome do Exercício'
    };
  }
  
  // Para refeições (meal)
  if (itemType === 'meal') {
    const hasCalories = rawJson.calories !== undefined;
    
    return {
      showValue: true,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      showCalories: hasCalories,
      textLabel: 'Alimentos'
    };
  }
  
  // Para notas de sessão (session_note)
  if (itemType === 'session_note') {
    return {
      showValue: false,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      textLabel: 'Nota da Sessão'
    };
  }
  
  // Para intervenções (intervention)
  if (itemType === 'intervention') {
    return {
      showValue: false,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      textLabel: 'Intervenção'
    };
  }
  
  // Para ajustes (adjustment)
  if (itemType === 'adjustment') {
    const hasPain = rawJson.pre_adjustment_pain !== undefined;
    
    return {
      showValue: true,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: false,
      showPain: hasPain,
      textLabel: 'Área Ajustada'
    };
  }
  
  // Para protocolos (protocol)
  if (itemType === 'protocol') {
    return {
      showValue: true,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: false,
      textLabel: 'Procedimento'
    };
  }
  
  // Para avaliações (assessment)
  if (itemType === 'assessment') {
    return {
      showValue: false,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      textLabel: 'Avaliação'
    };
  }
  
  // Para achados e conclusões (finding, conclusion)
  if (itemType === 'finding' || itemType === 'conclusion') {
    return {
      showValue: false,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: true,
      textLabel: itemType === 'finding' ? 'Achados' : 'Conclusão'
    };
  }
  
  // Para informações do plano (plan_info)
  if (itemType === 'plan_info') {
    const fieldName = item.field_name || '';
    // Determinar o label baseado no field_name
    let textLabel = 'Informação do Plano';
    if (fieldName === 'plan_name') {
      textLabel = 'Nome do Plano';
    } else if (fieldName === 'day_of_week') {
      textLabel = 'Dia da Semana';
    } else if (fieldName === 'focus') {
      textLabel = 'Foco';
    }
    
    return {
      showValue: true,
      showUnit: false,
      showRefLow: false,
      showRefHigh: false,
      showAbnormal: false,
      valueType: 'text' as const,
      showTextarea: false,
      textLabel: textLabel
    };
  }
  
  // Fallback padrão: texto simples
  return {
    showValue: true,
    showUnit: false,
    showRefLow: false,
    showRefHigh: false,
    showAbnormal: false,
    valueType: item.value_numeric !== null ? 'numeric' as const : 'text' as const,
    showTextarea: false
  };
}

interface HealthRecord {
  id: string;
  type: string;
  title: string;
  content?: string | null;
  text?: string | null;
  risk: string;
  ai_summary?: string | null;
  observed_at: string;
  metadata?: Record<string, any> | null;
  source_session_id?: string | null;
  document_id?: string | null;
  extracted_content?: string | null;
  is_session_container?: boolean;
}

interface RecordDetailsProps {
  record: HealthRecord;
  onViewDocument?: (id: string, type: 'smartfile' | 'document') => void;
}

interface HealthRecordItem {
  id: string;
  item_type: string;
  display_name: string;
  value_numeric: number | null;
  value_text: string | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  is_abnormal: boolean;
  observed_at: string;
  raw_json: any;
  field_name?: string;
  display_order?: number;
}

interface EditableFields {
  showValue: boolean;
  showUnit: boolean;
  showRefLow: boolean;
  showRefHigh: boolean;
  showAbnormal: boolean;
  valueType: 'numeric' | 'text';
  showTextarea?: boolean;
  textLabel?: string;
  showLoad?: boolean;
  showSets?: boolean;
  showReps?: boolean;
  showCalories?: boolean;
  showPain?: boolean;
}

interface EditableItemProps {
  item: HealthRecordItem;
  onSave: (updatedItem: HealthRecordItem) => Promise<void>;
  onDelete?: (itemId: string) => Promise<void>;
}

function EditableItem({ item, onSave, onDelete, recordDate }: EditableItemProps & { recordDate: string }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState(item);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedItem);
      setIsEditing(false);
      toast.success("Item atualizado com sucesso!");
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error("Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedItem(item);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(item.id);
      toast.success("Item excluído com sucesso!");
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error("Erro ao excluir item");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatContent = () => {
    if (item.value_numeric !== null) {
      return (
        <span>
          <span className="font-semibold">{item.value_numeric}</span>
          {item.unit && <span className="text-muted-foreground ml-1">{item.unit}</span>}
          {(item.ref_low !== null || item.ref_high !== null) && (
            <span className="text-xs text-muted-foreground ml-2">
              (Ref: {item.ref_low ?? '–'} - {item.ref_high ?? '–'})
            </span>
          )}
        </span>
      );
    }
    return <span>{item.value_text}</span>;
  };

  if (isEditing) {
    const fields = getEditableFields(item);
    const rawJson = (item.raw_json && typeof item.raw_json === 'object' && !Array.isArray(item.raw_json)) 
      ? (item.raw_json as Record<string, any>) 
      : {};
    
    return (
      <tr className="border-b border-border">
        <td colSpan={4} className="p-3 bg-muted/30">
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Campo de valor/texto principal */}
              {(fields.showValue || fields.showTextarea) && (
                <div className={fields.showTextarea ? "md:col-span-2 lg:col-span-3" : ""}>
                  <Label htmlFor={`value-${item.id}`} className="text-xs">
                    {fields.textLabel || (fields.valueType === 'numeric' ? 'Valor' : 'Texto')}
                  </Label>
                  {fields.showTextarea ? (
                    <Textarea
                      id={`value-${item.id}`}
                      value={editedItem.value_text || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, value_text: e.target.value })}
                      className="min-h-[100px]"
                      placeholder={fields.textLabel || "Digite o texto..."}
                    />
                  ) : (
                    <Input
                      id={`value-${item.id}`}
                      type={fields.valueType === 'numeric' ? "number" : "text"}
                      value={fields.valueType === 'numeric' 
                        ? (editedItem.value_numeric ?? '') 
                        : (editedItem.value_text || '')}
                      onChange={(e) => {
                        if (fields.valueType === 'numeric') {
                          setEditedItem({
                            ...editedItem,
                            value_numeric: e.target.value ? parseFloat(e.target.value) : null
                          });
                        } else {
                          setEditedItem({ ...editedItem, value_text: e.target.value });
                        }
                      }}
                      className="h-8"
                    />
                  )}
                </div>
              )}

              {/* Unidade */}
              {fields.showUnit && (
                <div>
                  <Label htmlFor={`unit-${item.id}`} className="text-xs">Unidade</Label>
                  <Input
                    id={`unit-${item.id}`}
                    value={editedItem.unit || ''}
                    onChange={(e) => setEditedItem({ ...editedItem, unit: e.target.value })}
                    placeholder="kg, cm, mg/dL..."
                    className="h-8"
                  />
                </div>
              )}

              {/* Referência Mínima */}
              {fields.showRefLow && (
                <div>
                  <Label htmlFor={`ref-low-${item.id}`} className="text-xs">Ref. Mínima</Label>
                  <Input
                    id={`ref-low-${item.id}`}
                    type="number"
                    value={editedItem.ref_low ?? ''}
                    onChange={(e) => setEditedItem({ ...editedItem, ref_low: e.target.value ? parseFloat(e.target.value) : null })}
                    className="h-8"
                  />
                </div>
              )}

              {/* Referência Máxima */}
              {fields.showRefHigh && (
                <div>
                  <Label htmlFor={`ref-high-${item.id}`} className="text-xs">Ref. Máxima</Label>
                  <Input
                    id={`ref-high-${item.id}`}
                    type="number"
                    value={editedItem.ref_high ?? ''}
                    onChange={(e) => setEditedItem({ ...editedItem, ref_high: e.target.value ? parseFloat(e.target.value) : null })}
                    className="h-8"
                  />
                </div>
              )}

              {/* Campos específicos para exercícios */}
              {fields.showLoad && (
                <>
                  <div>
                    <Label htmlFor={`load-${item.id}`} className="text-xs">Carga</Label>
                    <Input
                      id={`load-${item.id}`}
                      type="number"
                      value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).load : null) ?? (rawJson as Record<string, any>).load ?? '')}
                      onChange={(e) => {
                        const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                          ? (editedItem.raw_json as Record<string, any>)
                          : {};
                        const updatedRawJson = { ...currentJson, load: e.target.value ? parseFloat(e.target.value) : null };
                        setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`load-unit-${item.id}`} className="text-xs">Unidade de Medida</Label>
                    <Select
                      value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).load_unit : null) ?? (rawJson as Record<string, any>).load_unit ?? 'Kg')}
                      onValueChange={(value) => {
                        const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                          ? (editedItem.raw_json as Record<string, any>)
                          : {};
                        const updatedRawJson = { ...currentJson, load_unit: value || null };
                        setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                      }}
                    >
                      <SelectTrigger className="h-8" id={`load-unit-${item.id}`}>
                        <SelectValue placeholder="Selecione a unidade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="Un">Un</SelectItem>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="s">s</SelectItem>
                        <SelectItem value="°">°</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {fields.showSets && (
                <div>
                  <Label htmlFor={`sets-${item.id}`} className="text-xs">Séries</Label>
                  <Input
                    id={`sets-${item.id}`}
                    type="number"
                    value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).sets : null) ?? (rawJson as Record<string, any>).sets ?? '')}
                    onChange={(e) => {
                      const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                        ? (editedItem.raw_json as Record<string, any>)
                        : {};
                      const updatedRawJson = { ...currentJson, sets: e.target.value ? parseInt(e.target.value) : null };
                      setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                    }}
                    className="h-8"
                  />
                </div>
              )}

              {fields.showReps && (
                <div>
                  <Label htmlFor={`reps-${item.id}`} className="text-xs">Repetições</Label>
                  <Input
                    id={`reps-${item.id}`}
                    type="text"
                    value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).reps : null) ?? (rawJson as Record<string, any>).reps ?? '')}
                    onChange={(e) => {
                      const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                        ? (editedItem.raw_json as Record<string, any>)
                        : {};
                      const updatedRawJson = { ...currentJson, reps: e.target.value };
                      setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                    }}
                    className="h-8"
                    placeholder="10 a 12"
                  />
                </div>
              )}

              {/* Calorias para refeições */}
              {fields.showCalories && (
                <div>
                  <Label htmlFor={`calories-${item.id}`} className="text-xs">Calorias (kcal)</Label>
                  <Input
                    id={`calories-${item.id}`}
                    type="number"
                    value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).calories : null) ?? (rawJson as Record<string, any>).calories ?? '')}
                    onChange={(e) => {
                      const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                        ? (editedItem.raw_json as Record<string, any>)
                        : {};
                      const updatedRawJson = { ...currentJson, calories: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                    }}
                    className="h-8"
                  />
                </div>
              )}

              {/* Dor pré-ajuste para ajustes quiropráticos */}
              {fields.showPain && (
                <div>
                  <Label htmlFor={`pain-${item.id}`} className="text-xs">Dor Pré-Ajuste (0-10)</Label>
                  <Input
                    id={`pain-${item.id}`}
                    type="number"
                    min="0"
                    max="10"
                    value={String((editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json) ? (editedItem.raw_json as Record<string, any>).pre_adjustment_pain : null) ?? (rawJson as Record<string, any>).pre_adjustment_pain ?? '')}
                    onChange={(e) => {
                      const currentJson = (editedItem.raw_json && typeof editedItem.raw_json === 'object' && !Array.isArray(editedItem.raw_json)) 
                        ? (editedItem.raw_json as Record<string, any>)
                        : {};
                      const updatedRawJson = { ...currentJson, pre_adjustment_pain: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedItem({ ...editedItem, raw_json: updatedRawJson });
                    }}
                    className="h-8"
                  />
                </div>
              )}

              {/* Switch Anormal */}
              {fields.showAbnormal && (
                <div className="flex items-center gap-2 pt-4">
                  <Switch
                    id={`abnormal-${item.id}`}
                    checked={editedItem.is_abnormal || false}
                    onCheckedChange={(checked) => setEditedItem({ ...editedItem, is_abnormal: checked })}
                  />
                  <Label htmlFor={`abnormal-${item.id}`} className="text-xs">Anormal</Label>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Salvando..." : "Salvar"}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                Cancelar
              </Button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-border hover:bg-muted/30 transition-colors">
      <td className="p-3 text-sm text-muted-foreground">
        {format(new Date(item.observed_at || recordDate), "dd/MM/yyyy")}
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs h-5">
            {item.item_type ? (TYPE_LABELS[item.item_type] || TYPE_LABELS[item.item_type.toLowerCase()] || item.item_type) : 'Sem tipo'}
          </Badge>
          {item.is_abnormal && (
            <Badge variant="destructive" className="text-xs h-5">
              Anormal
            </Badge>
          )}
        </div>
      </td>
      <td className="p-3">
        <div className="space-y-1">
          <div className="text-sm font-medium">{item.display_name}</div>
          <div className="text-sm text-foreground">{formatContent()}</div>
        </div>
      </td>
      <td className="p-3 text-right">
        <div className="flex gap-1 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-7 px-2"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2">
                <Database className="h-3 w-3" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Dados Brutos - {item.display_name}</DialogTitle>
              </DialogHeader>
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(item.raw_json, null, 2)}
              </pre>
            </DialogContent>
          </Dialog>
          {onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={isDeleting}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.
                    <br />
                    <span className="font-medium mt-2 block">{item.display_name}</span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? "Excluindo..." : "Excluir"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
}

export function RecordDetails({ record }: RecordDetailsProps) {
  // ✅ REMOVIDO: health_record_items não é mais usado
  const { data: recordItems } = useQuery({
    queryKey: ["health-record-items", record.id],
    queryFn: async () => {
      return [];
    }
  });

  // ✅ REMOVIDO: health_record_items não é mais usado
  const handleSaveItem = async (updatedItem: HealthRecordItem) => {
    // Não fazer nada - tabela não é mais usada
  };

  const handleDeleteItem = async (itemId: string) => {
    // Não fazer nada - tabela não é mais usada
  };

  if (!recordItems || recordItems.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Nenhum dado extraído disponível</p>
      </div>
    );
  }

  // Agrupar items por data observada
  const itemsByDate = recordItems.reduce((acc, item) => {
    const date = item.observed_at || record.observed_at;
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {} as Record<string, HealthRecordItem[]>);

  const sortedDates = Object.keys(itemsByDate).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div className="bg-background border-t">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Data
              </th>
              <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Tipo
              </th>
              <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Conteúdo
              </th>
              <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDates.map((date, dateIndex) => (
              <React.Fragment key={date}>
                {/* Separador de sessão */}
                <tr className="bg-primary/5 border-y-2 border-primary/20">
                  <td colSpan={4} className="p-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                      <span>📅 Sessão {dateIndex + 1} de {sortedDates.length}</span>
                      <span className="text-muted-foreground">
                        {format(new Date(date), "dd/MM/yyyy")}
                      </span>
                      <Badge variant="secondary" className="ml-auto">
                        {itemsByDate[date].length} items
                      </Badge>
                    </div>
                  </td>
                </tr>
                
                {/* Items da sessão */}
                {itemsByDate[date].map((item) => (
                  <EditableItem 
                    key={item.id} 
                    item={item} 
                    onSave={handleSaveItem}
                    onDelete={handleDeleteItem}
                    recordDate={item.observed_at || record.observed_at}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {recordItems.length} item(ns) extraído(s) em {sortedDates.length} sessão(ões)
      </div>
    </div>
  );
}
